import { Injectable, inject } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import {
  catchError,
  delay,
  endWith,
  map,
  of,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs'
import { distinctUntilChanged, filter } from 'rxjs/operators'

import { BpmnAiAnalystService } from '@flowx/designer/modules/bpmn-editor/bpmn-ai-analyst.service'

import { AiAnalystActions } from './analyst.actions'

import { DummyAiDelayTime } from '../../../common/ai.dictionary'
// eslint-disable-next-line import/no-cycle
import { AiActions } from '../../../store/ai.actions'
import { AiSelectors } from '../../../store/ai.selectors'
import {
  AiAnalystAPI,
  GenerateFromSequenceRequest,
  GenerateProcessRequest,
  GenerateProcessResponse,
} from '../api/analyst.api'
import { AiAnalystUtils, AiProcessEntities } from '../services/analyst.service'

@Injectable()
export class AiAnalystEffects {
  #actions$ = inject(Actions)
  #store = inject(Store)
  #analystApi = inject(AiAnalystAPI)
  #bpmnAiAnalystService = inject(BpmnAiAnalystService)

  generate = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.generate),
      withLatestFrom(this.#store.select(AiSelectors.startedFromSequence)),
      map(([prompt, startedFromSequence]) => {
        if (startedFromSequence) {
          return AiAnalystActions.insertInProcess(prompt)
        } else {
          return AiAnalystActions.generateProcess(prompt)
        }
      })
    )
  )

  extractProcessFromDocument = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.extractProcessFromDocument),
      switchMap(({ documentContextReference }) =>
        this.#analystApi.extractProcessFromDocument(documentContextReference).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiAnalystActions.generateProcess({ prompt: response.response })),
          catchError(({ status, error }) =>
            of(AiAnalystActions.generateProcessFailure({ statusCode: status, errorMsg: error }))
          )
        )
      )
    )
  )

  generateProcessFromImages = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.generateProcessFromImages),
      withLatestFrom(this.#store.select(AiSelectors.sessionId)),
      map(([{ files, prompt }, sessionId]) =>
        this.#mapImagePromptToFormData(files[0], prompt, sessionId)
      ),
      switchMap((formData) =>
        this.#analystApi.generateProcessFromImages(formData).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => {
            if (response.image_type?.image_type !== 'business_process') {
              throw new Error(
                `The image cannot be used for process generation. Please upload a image with business process. \n \n ${response.image_type?.summary} \n \n`
              )
            }
            return AiAnalystActions.generateProcessSuccess(
              this.#mapGenerateProcessResponse(response)
            )
          }),
          catchError(({ status, error, message }) =>
            of(
              AiAnalystActions.generateProcessFailure({
                statusCode: status,
                errorMsg: message ?? error,
              })
            )
          )
        )
      )
    )
  )

  generateProcessFromImagesStream = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.generateProcessFromImagesStream),
      withLatestFrom(this.#store.select(AiSelectors.sessionId)),
      map(([{ files, prompt }, sessionId]) =>
        this.#mapImagePromptToFormData(files[0], prompt, sessionId)
      ),
      switchMap((formData) =>
        this.#analystApi.getSSEGenerateProcess(formData).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          switchMap((response) => of(AiAnalystActions.getAnswerEventStream({ response }))),
          catchError(({ status, error, message }) =>
            of(
              AiAnalystActions.generateProcessFailure({
                statusCode: status,
                errorMsg: message ?? error,
              })
            )
          )
        )
      )
    )
  )

  generateProcessFromBpmnFiles = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.generateProcessFromBpmnFiles),
      withLatestFrom(this.#store.select(AiSelectors.sessionId)),
      map(([{ files }, sessionId]) => this.#mapBpmnFileToFormData(files[0], sessionId)),
      switchMap((formData) =>
        this.#analystApi.extractProcessFromBpmnFile(formData).pipe(
          delay(DummyAiDelayTime),
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) =>
            AiAnalystActions.generateProcessSuccess(this.#mapGenerateProcessResponse(response))
          ),
          catchError(({ status, error }) =>
            of(AiAnalystActions.generateProcessFailure({ statusCode: status, errorMsg: error }))
          )
        )
      )
    )
  )

  generateProcess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.generateProcess),
      withLatestFrom(
        this.#store.select(AiSelectors.generatedProcess),
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.requestId)
      ),
      map(
        ([{ prompt }, generatedProcess, sessionId, requestId]) =>
          ({
            question: prompt,
            context: generatedProcess ?? AiAnalystUtils.getInitialProcessContext(),
            session_id: sessionId,
            request_id: requestId,
          }) satisfies GenerateProcessRequest
      ),
      switchMap((payload) =>
        this.#analystApi.generateProcess(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) =>
            AiAnalystActions.generateProcessSuccess(this.#mapGenerateProcessResponse(response))
          ),
          catchError(({ status, error }) =>
            of(AiAnalystActions.generateProcessFailure({ statusCode: status, errorMsg: error }))
          )
        )
      )
    )
  )

  generateProcessStream = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiAnalystActions.generateProcessStream),
        withLatestFrom(
          this.#store.select(AiSelectors.generatedProcess),
          this.#store.select(AiSelectors.sessionId),
          this.#store.select(AiSelectors.requestId),
          this.#store.select(AiSelectors.currentPlanStep)
        ),
        map(
          ([{ prompt }, generatedProcess, sessionId, requestId, currentPlanStep]) =>
            ({
              question: prompt,
              context: generatedProcess ?? AiAnalystUtils.getInitialProcessContext(),
              session_id: sessionId,
              request_id: requestId,
              _fromPlanner: !!currentPlanStep,
            }) satisfies GenerateProcessRequest & { _fromPlanner: boolean }
        ),
        switchMap((payload) => {
          const formData = new FormData()
          formData.append('prompt', payload.question)
          formData.append('context', JSON.stringify(payload.context))
          formData.append('session_id', payload.session_id)
          if (payload._fromPlanner) {
            formData.append('skip_greeting', 'true')
          }
          return this.#analystApi.getSSEGenerateProcess(formData).pipe(
            map((response) => AiAnalystActions.getAnswerEventStream({ response })),
            catchError(({ status }) =>
              of(
                AiActions.getAnswerFailure({
                  statusCode: status,
                  errorMsg: null,
                })
              )
            ),
            endWith(AiActions.getAnswerStreamCompleted())
          )
        })
      ),
    { dispatch: true }
  )

  convertBPMN = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.convertBPMN),
      withLatestFrom(this.#store.select(AiSelectors.sessionId)),
      map(([{ processFiles }, sessionId]) => this.#mapBPMNToFormData(processFiles[0], sessionId)),
      switchMap((formData) =>
        this.#analystApi.convertBPMN(formData).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) =>
            AiAnalystActions.convertBPMNSuccess(this.#mapGenerateProcessResponse(response))
          ),
          catchError(({ status, error }) =>
            of(AiAnalystActions.convertBPMNFailure({ statusCode: status, errorMsg: error }))
          )
        )
      )
    )
  )

  insertInProcess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.insertInProcess),

      withLatestFrom(
        this.#store.select(AiSelectors.generatedSequenceProcess),
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.requestId),
        this.#store.select(AiSelectors.startedFromSequence)
      ),

      map(([{ prompt }, generatedProcess, sessionId, requestId, startedFromSequence]) => {
        const processState = AiAnalystUtils.getProcessStateSnapshot()
        const initialContext = AiAnalystUtils.getInitialProcessSequenceContext(
          processState,
          startedFromSequence
        )
        return {
          payload: {
            question: prompt,
            context: generatedProcess ?? initialContext.context,
            start_node_id: initialContext.startNodeId,
            end_node_id: initialContext.endNodeId,
            offset: initialContext.offset,
            session_id: sessionId,
            request_id: requestId,
          } satisfies GenerateFromSequenceRequest,
          params: {
            initialContext,
            processState,
          },
        }
      }),

      switchMap(({ payload, params }) =>
        this.#analystApi.insert(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => {
            let aiEntities: AiProcessEntities

            try {
              aiEntities = AiAnalystUtils.mergeUpdatesWithExistingProcess(params.processState, {
                aiProcess: response.answer,
                startNodeId: params.initialContext.startNodeId,
                endNodeId: params.initialContext.endNodeId,
                startNodeSwimlaneUuid: params.initialContext.startSwimlaneUuid,
                endNodeSwimlaneUuid: params.initialContext.endSwimlaneUuid,
                swimlanesUuidToId: params.initialContext.swimlanesUuidToId,
              })
            } catch (error) {
              if (!response.chat) {
                console.error('I couldn’t generate a valid response. Please try again.', error)
              }
            }

            return AiAnalystActions.generateProcessSuccess({
              response,
              aiEntities,
            })
          }),
          catchError(({ status, error }) =>
            of(AiAnalystActions.generateProcessFailure({ statusCode: status, errorMsg: error }))
          )
        )
      )
    )
  )

  onGenerateProcessDraft = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiAnalystActions.generateProcessDraft),
        tap(({ aiEntities }) => {
          this.#bpmnAiAnalystService.renderAiProcessEntities(aiEntities)
        })
      ),
    { dispatch: false }
  )

  onGenerateProcessSuccess = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiAnalystActions.generateProcessSuccess),
        tap(({ aiEntities }) => {
          this.#bpmnAiAnalystService.renderAiProcessEntities(aiEntities)
        })
      ),
    { dispatch: false }
  )

  onConvertBPMNSuccess = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiAnalystActions.convertBPMNSuccess),
        tap(({ aiEntities }) => this.#bpmnAiAnalystService.renderAiProcessEntities(aiEntities))
      ),
    { dispatch: false }
  )

  discardProcessDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.discardProcessDraft, AiActions.startNewSession),
      tap(() => this.#bpmnAiAnalystService.discardDraft()),
      map(() => AiAnalystActions.discardProcessDraftSuccess())
    )
  )

  keepProcessDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.keepProcessDraft),
      withLatestFrom(this.#store.select(AiSelectors.generatedProcessEntities)),
      switchMap(([, generatedProcessEntities]) => {
        if (!generatedProcessEntities) {
          return of(
            AiAnalystActions.keepProcessDraftFailure({
              statusCode: 400,
              errorMsg: 'No generated process entities available',
            })
          )
        }
        return this.#bpmnAiAnalystService.keepDraft(generatedProcessEntities).pipe(
          map((response) => AiAnalystActions.keepProcessDraftSuccess({ response })),
          catchError(({ status, error }) =>
            of(
              AiAnalystActions.keepProcessDraftFailure({
                statusCode: status,
                errorMsg: error,
              })
            )
          )
        )
      })
    )
  )

  continueKeepActionEvents = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.keepProcessDraftSuccess),
      withLatestFrom(this.#store.select(AiSelectors.currentPlanStep)),
      filter(([, currentPlanStep]) => currentPlanStep === null),
      switchMap(() => {
        const currentProcess = AiAnalystUtils.getInitialProcessContext()
        return this.#analystApi
          .getSSEActions({
            continue_editing: true,
            action_type: 'keep_business_process',
            context: currentProcess,
          })
          .pipe(
            takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
            switchMap((response) => of(AiAnalystActions.getAnswerEventStream({ response }))),
            catchError(({ status }) =>
              of(
                AiActions.getAnswerFailure({
                  statusCode: status,
                  errorMsg: null,
                })
              )
            ),
            endWith(AiActions.getAnswerStreamCompleted())
          )
      })
    )
  )

  continueDiscardActionEvents = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.discardProcessDraftSuccess),
      switchMap(() => {
        const currentProcess = AiAnalystUtils.getInitialProcessContext()
        return this.#analystApi
          .getSSEActions({
            continue_editing: true,
            action_type: 'discard_business_process',
            context: currentProcess,
          })
          .pipe(
            takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
            switchMap((response) => of(AiAnalystActions.getAnswerEventStream({ response }))),
            catchError(({ status }) =>
              of(
                AiActions.getAnswerFailure({
                  statusCode: status,
                  errorMsg: null,
                })
              )
            ),
            endWith(AiActions.getAnswerStreamCompleted())
          )
      })
    )
  )

  sendFeedback = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiAnalystActions.sendFeedback),
        switchMap(({ payload }) => this.#analystApi.sendFeedback(payload))
      ),
    { dispatch: false }
  )

  temporaryProcessChanged = createEffect(
    () =>
      this.#store.select(AiSelectors.temporaryProcessEntities).pipe(
        distinctUntilChanged(),
        filter((temporaryProcessEntities) => temporaryProcessEntities !== null),
        tap((temporaryProcessEntities) => {
          this.#store.dispatch(
            AiAnalystActions.generateProcessDraft({ aiEntities: temporaryProcessEntities })
          )
          // Optional: dispatch additional actions if needed
        })
      ),
    { dispatch: false }
  )

  generatedProcessChanged = createEffect(
    () =>
      this.#store.select(AiSelectors.generatedProcessEntities).pipe(
        withLatestFrom(
          this.#store.select(AiSelectors.generatedProcess),
          this.#store.select(AiSelectors.isZenMode),
          this.#store.select(AiSelectors.sessionId),
          this.#store.select(AiSelectors.requestId)
        ),
        distinctUntilChanged(),
        filter(([generatedProcessEntities]) => generatedProcessEntities !== null),
        tap(([generatedProcessEntities, generatedProcess, isZenMode, sessionId, requestId]) => {
          if (isZenMode) {
            this.#store.dispatch(
              AiAnalystActions.generateProcessSuccess({
                response: {
                  answer: generatedProcess,
                  question: null,
                  chat: null,
                  context: null,
                  session_id: sessionId,
                  request_id: requestId,
                  error_message: null,
                },
                aiEntities: generatedProcessEntities,
              })
            )
          }
        })
      ),
    { dispatch: false }
  )

  #mapBpmnFileToFormData(bpmnFile: File, sessionId: string): FormData {
    const formData = new FormData()
    formData.append('bpmn_file', bpmnFile)
    formData.append('session_id', sessionId)
    return formData
  }

  #mapImagePromptToFormData(imageFile: File, prompt: string, sessionId: string): FormData {
    const formData = new FormData()
    formData.append('image_file', imageFile)
    formData.append('prompt', prompt)
    formData.append('session_id', sessionId)
    return formData
  }

  #mapBPMNToFormData(bpmnFile: File, sessionId: string): FormData {
    const formData = new FormData()
    formData.append('bpmn_file', bpmnFile)
    formData.append('session_id', sessionId)
    return formData
  }

  #mapGenerateProcessResponse(response: GenerateProcessResponse): {
    response: GenerateProcessResponse
    aiEntities: AiProcessEntities
  } {
    let aiEntities: AiProcessEntities
    try {
      aiEntities = AiAnalystUtils.extractAiEntitiesFromResponse(
        response,
        AiAnalystUtils.getProcessStateSnapshot()
      )
    } catch (e) {
      if (!response.chat) {
        console.error('I couldn’t generate a valid response. Please try again.', e)
      }
    }
    return {
      response,
      aiEntities,
    }
  }
}
